import { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search,
  Menu,
  LayoutDashboard,
  Inbox,
  Users,
  FolderKanban,
  FileText,
  Layers,
  BookOpen,
  Store,
  UsersRound,
  Settings,
  HelpCircle,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import client, { unwrap } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ROOM_STOCK_IMAGES } from "@/lib/stockImages";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import NotificationBell from "@/components/notifications/NotificationBell";
import UserMenu from "./UserMenu";

const NAV_GROUPS = [
  {
    label: "SALES",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/enquiries", label: "Enquiries", icon: Inbox, permission: "enquiry:manage", badgeKey: "newEnquiries" },
      { to: "/customers", label: "Customers", icon: Users, permission: "customer:manage" },
      { to: "/projects", label: "Projects", icon: FolderKanban, permission: "project:manage" },
      { to: "/quotations", label: "Quotations", icon: FileText, permission: "quotation:create" },
    ],
  },
  {
    label: "CATALOGUE",
    items: [
      { to: "/products", label: "Products", icon: Layers, permission: "product:read" },
      { to: "/catalogues", label: "Catalogues", icon: BookOpen, permission: "catalogue:read" },
    ],
  },
  {
    label: "MANAGE",
    items: [
      { to: "/showrooms", label: "Showrooms", icon: Store, permission: "showroom:manage" },
      { to: "/team", label: "Team", icon: UsersRound, permission: "user:manage" },
    ],
  },
];

const BOTTOM_NAV = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/quotations", label: "Quotes", icon: FileText },
];

// Below `lg` the desktop sidebar collapses to an icon rail; the mobile sheet
// passes `expanded` so it always shows labels.
function railClasses(expanded) {
  return expanded
    ? { label: "inline", block: "block", flex: "flex", inlineFlex: "inline-flex", railOnly: "hidden" }
    : { label: "lg:inline hidden", block: "lg:block hidden", flex: "lg:flex hidden", inlineFlex: "lg:inline-flex hidden", railOnly: "lg:hidden" };
}

function NavItem({ to, label, icon: Icon, onNavigate, badge, expanded }) {
  const rc = railClasses(expanded);
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      title={label}
      data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] transition-colors duration-200 ${
          isActive
            ? "bg-primary text-primary-foreground font-medium shadow-sm"
            : "text-foreground/80 hover:bg-border/60 hover:text-foreground"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
          <span className={`${rc.label} flex-1`}>{label}</span>
          {badge > 0 && (
            <>
              <span
                className={`${rc.inlineFlex} min-w-[1.5rem] justify-center rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums ${
                  isActive ? "bg-primary-foreground/20" : "bg-warning/20 text-[hsl(41_60%_30%)]"
                }`}
              >
                {badge > 99 ? "99+" : badge}
              </span>
              <span className={`${rc.railOnly} absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-accent`} aria-hidden="true" />
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

function Wordmark({ compact = false }) {
  return (
    <div className="leading-none">
      <span className="font-serif text-[1.35rem] tracking-tight text-foreground">
        HOME<span className="text-accent">INCLUDES</span>
      </span>
      {!compact && (
        <p className="mt-1.5 text-[9px] font-medium tracking-[0.25em] text-muted-foreground">SPACES MADE FOR YOU</p>
      )}
    </div>
  );
}

function SidebarContent({ groups, isPlatformAdmin, onNavigate, badges = {}, expanded = false }) {
  const rc = railClasses(expanded);
  const footerButton =
    "w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-foreground/80 hover:bg-border/60 hover:text-foreground transition-colors";
  return (
    <div className="flex h-full flex-col">
      <div className="px-4 lg:px-6 py-6">
        <div className={rc.block}><Wordmark /></div>
        <span className={`${rc.railOnly} font-serif text-xl text-foreground`}>F<span className="text-accent">K</span></span>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 lg:px-4 pb-4">
        <div className="mb-5">
          {groups.map((group, gi) => (
            <div key={group.label} className={gi > 0 ? "mt-6" : ""}>
              {gi > 0 && (
                <p className={`${rc.block} px-3 mb-2 text-[11px] font-semibold tracking-widest text-muted-foreground uppercase`}>
                  {group.label}
                </p>
              )}
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavItem key={item.to} {...item} badge={badges[item.badgeKey]} expanded={expanded} onNavigate={onNavigate} />
                ))}
              </div>
            </div>
          ))}
        </div>
        {isPlatformAdmin && (
          <div className="mb-5">
            <p className={`${rc.block} px-3 mb-2 text-[11px] font-semibold tracking-widest text-muted-foreground uppercase`}>
              PLATFORM
            </p>
            <NavItem to="/platform" label="Platform Admin" icon={ShieldCheck} expanded={expanded} onNavigate={onNavigate} />
          </div>
        )}
      </nav>
      <div className="border-t border-border px-2 lg:px-4 py-4 space-y-1">
        <button data-testid="nav-settings-link" onClick={() => toast("Settings — coming soon")} className={footerButton}>
          <Settings className="h-4 w-4 shrink-0" strokeWidth={1.75} />
          <span className={rc.label}>Settings</span>
        </button>
        <button data-testid="nav-help-link" onClick={() => toast("Help center — coming soon")} className={footerButton}>
          <HelpCircle className="h-4 w-4 shrink-0" strokeWidth={1.75} />
          <span className={rc.label}>Help &amp; Support</span>
        </button>
        <NavLink
          to="/products"
          onClick={onNavigate}
          data-testid="sidebar-promo-card"
          className={`${rc.flex} relative mt-3 h-20 items-center overflow-hidden rounded-xl bg-secondary px-4 group`}
        >
          <img
            src={ROOM_STOCK_IMAGES.living}
            alt=""
            className="absolute right-0 inset-y-0 h-full w-1/2 object-cover opacity-80"
          />
          <span className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--secondary))] via-[hsl(var(--secondary)/0.85)] to-transparent" />
          <span className="relative font-serif text-[15px] leading-tight text-foreground">
            Design
            <br />
            Better Spaces
          </span>
          <span className="relative ml-auto h-7 w-7 rounded-full bg-card/90 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
            <ArrowRight className="h-3.5 w-3.5 text-foreground" />
          </span>
        </NavLink>
      </div>
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["global-search", q],
    queryFn: async () => {
      const [customers, projects] = await Promise.all([
        client.get("/customers").then(unwrap).catch(() => []),
        client.get("/projects").then(unwrap).catch(() => []),
      ]);
      const term = q.trim().toLowerCase();
      return {
        customers: customers.filter((c) => c.name?.toLowerCase().includes(term)).slice(0, 4),
        projects: projects.filter((p) => p.name?.toLowerCase().includes(term)).slice(0, 4),
      };
    },
    enabled: q.trim().length > 1,
  });

  const results =
    data && q.trim().length > 1
      ? [
          ...(data.customers || []).map((c) => ({ type: "Customer", label: c.name, to: `/customers/${c.id || c._id}` })),
          ...(data.projects || []).map((p) => ({ type: "Project", label: p.name, to: `/projects/${p.id || p._id}` })),
        ]
      : [];

  return (
    <div className="relative flex-1 max-w-xl">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search customers and projects…"
        className="w-full rounded-full border border-border bg-secondary/70 pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20 transition-shadow"
      />
      {open && results.length > 0 && (
        <div
          data-testid="global-search-results"
          className="absolute z-40 mt-1.5 w-full rounded-xl border border-border bg-card shadow-lg overflow-hidden hi-animate-fade-up"
        >
          {results.map((r, idx) => (
            <button
              key={idx}
              data-testid={`search-result-${idx}`}
              onClick={() => {
                navigate(r.to);
                setQ("");
                setOpen(false);
              }}
              className="w-full flex items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-secondary transition-colors"
            >
              <span className="text-foreground">{r.label}</span>
              <span className="text-xs text-muted-foreground">{r.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const BANNER_COPY = {
  PENDING_VERIFICATION: "Your shop is awaiting verification. Most actions unlock once a Platform Admin approves it.",
  CHANGES_REQUESTED: "The Platform Admin asked for changes to your shop details. Update them and resubmit.",
  REJECTED: "Your shop registration was rejected.",
};

function VerificationBanner({ status }) {
  const { pathname } = useLocation();
  if (pathname === "/shop-verification") return null;
  return (
    <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="verification-banner">
      <p className="flex-1 text-sm text-foreground">{BANNER_COPY[status] || BANNER_COPY.PENDING_VERIFICATION}</p>
      <NavLink to="/shop-verification" className="text-sm font-medium text-accent hover:underline">
        {status === "CHANGES_REQUESTED" ? "Review and resubmit" : "View details"}
      </NavLink>
    </div>
  );
}

export function AppLayout() {
  const { hasPermission, isPlatformAdmin } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const { data: tenant } = useQuery({
    queryKey: ["tenant-me"],
    queryFn: () => client.get("/tenants/me").then(unwrap),
    enabled: !isPlatformAdmin(),
  });

  const canSeeEnquiries = hasPermission("enquiry:manage");
  const { data: enquiries } = useQuery({
    queryKey: ["enquiries"],
    queryFn: () => client.get("/enquiries").then(unwrap),
    enabled: canSeeEnquiries && !isPlatformAdmin(),
  });
  const badges = { newEnquiries: (enquiries || []).filter((e) => e.status === "NEW").length };

  const visibleGroups = useMemo(
    () =>
      NAV_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((item) => !item.permission || hasPermission(item.permission)),
      })).filter((g) => g.items.length > 0),
    [hasPermission],
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop / tablet sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 z-30 w-16 lg:w-64 flex-col border-r border-border bg-sidebar transition-all">
        <SidebarContent groups={visibleGroups} isPlatformAdmin={isPlatformAdmin()} badges={badges} />
      </aside>

      <div className="md:pl-16 lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/85 backdrop-blur-md px-4 md:px-6 lg:px-8 py-3">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-nav-trigger" aria-label="Open navigation" className="md:hidden p-2 -ml-2 text-foreground">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 bg-sidebar border-border">
              <SidebarContent
                groups={visibleGroups}
                isPlatformAdmin={isPlatformAdmin()}
                badges={badges}
                expanded
                onNavigate={() => setMobileNavOpen(false)}
              />
            </SheetContent>
          </Sheet>

          <span className="md:hidden"><Wordmark compact /></span>

          <div className="hidden sm:flex flex-1">
            <GlobalSearch />
          </div>

          <div className="flex items-center gap-2 sm:gap-3 ml-auto">
            {tenant?.city && (
              <div
                data-testid="showroom-selector-pill"
                className="hidden lg:flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2 text-sm text-foreground"
              >
                <Store className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <span className="truncate max-w-[10rem]">{tenant.city} Showroom</span>
              </div>
            )}

            {!isPlatformAdmin() && <NotificationBell />}

            <UserMenu />
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8 pb-24 md:pb-8">
          {tenant?.status && tenant.status !== "APPROVED" && <VerificationBanner status={tenant.status} />}
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 flex items-stretch border-t border-border bg-card">
        {BOTTOM_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`bottom-nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-xs transition-colors ${
                isActive ? "text-primary font-medium" : "text-muted-foreground"
              }`
            }
          >
            <item.icon className="h-5 w-5" strokeWidth={1.75} />
            {item.label}
          </NavLink>
        ))}
        <button
          data-testid="bottom-nav-more"
          onClick={() => setMobileNavOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-xs text-muted-foreground"
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} />
          More
        </button>
      </nav>
    </div>
  );
}

export default AppLayout;
